import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, Search, User, LogOut, LayoutDashboard, GraduationCap, Scissors } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/site/NotificationBell";
import { usePublicSettings } from "@/lib/usePublicSettings";
import { gaEvent } from "@/lib/analytics";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/store", label: "Courses" },
  { to: "/community", label: "Community" },
  { to: "/portfolio", label: "My Work" },
];

export function Navbar() {
  const { user, logout } = useAuth();
  const { logo, site_name } = usePublicSettings();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const isAdmin = user && user.role === "admin";

  const AccountMenu = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="account-menu-btn" className="flex items-center gap-2 rounded-sm border border-white/15 px-3 py-2 text-sm hover:border-[#DCA846]/50 transition-colors">
          {user.picture ? (
            <img src={user.picture} alt="" className="h-6 w-6 rounded-full object-cover" />
          ) : (
            <User className="h-4 w-4" />
          )}
          <span className="hidden sm:inline max-w-[120px] truncate">{user.name}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-zinc-950 border-white/10 text-white">
        <DropdownMenuItem data-testid="menu-my-learning" onClick={() => nav("/my-learning")}>
          <GraduationCap className="mr-2 h-4 w-4" /> My Learning
        </DropdownMenuItem>
        {isAdmin && (
          <DropdownMenuItem data-testid="menu-admin" onClick={() => nav("/admin")}>
            <LayoutDashboard className="mr-2 h-4 w-4" /> Admin Dashboard
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); nav("/"); }}>
          <LogOut className="mr-2 h-4 w-4" /> Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <header className="fixed top-0 z-50 w-full backdrop-blur-xl bg-black/60 border-b border-white/10">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 h-16">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
          {logo ? (
            <img src={logo.startsWith("http") ? logo : logo} alt={site_name || "Epic Fade Academy"} data-testid="site-logo" className="h-9 w-auto object-contain" />
          ) : (
            <>
              <Scissors className="h-6 w-6 text-[#DCA846] group-hover:rotate-12 transition-transform" />
              <span className="font-display text-lg sm:text-xl font-bold uppercase tracking-wider">
                Epic Fade <span className="text-[#DCA846]">Academy</span>
              </span>
            </>
          )}
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              onClick={() => { if (n.to === "/community") gaEvent("community_click", { source: "navbar" }); }}
              data-testid={`nav-${n.label.toLowerCase().replace(" ", "-")}`}
              className={`text-sm uppercase tracking-wider transition-colors ${
                loc.pathname === n.to ? "text-[#DCA846]" : "text-zinc-300 hover:text-white"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button data-testid="nav-search-btn" onClick={() => nav("/store")} className="hidden sm:block text-zinc-300 hover:text-[#DCA846]">
            <Search className="h-5 w-5" />
          </button>
          {user === null ? null : user ? (
            <>
              <NotificationBell />
              <AccountMenu />
            </>
          ) : (
            <div className="hidden sm:flex items-center gap-2">
              <Button data-testid="nav-login-btn" variant="ghost" className="text-white hover:bg-white/10" onClick={() => nav("/login")}>
                Log in
              </Button>
              <Button data-testid="nav-signup-btn" className="bg-[#DCA846] text-black hover:bg-[#C5963B] uppercase font-semibold rounded-sm" onClick={() => nav("/register")}>
                Get Started
              </Button>
            </div>
          )}

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-btn" className="md:hidden text-white">
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-zinc-950 border-white/10 text-white w-72">
              <div className="mt-8 flex flex-col gap-1">
                {NAV.map((n) => (
                  <Link key={n.to} to={n.to} onClick={() => { setOpen(false); if (n.to === "/community") gaEvent("community_click", { source: "navbar_mobile" }); }}
                    className="py-3 text-lg font-display uppercase tracking-wide border-b border-white/5">
                    {n.label}
                  </Link>
                ))}
                {user ? (
                  <>
                    <Link to="/my-learning" onClick={() => setOpen(false)} className="py-3 text-lg font-display uppercase tracking-wide border-b border-white/5">My Learning</Link>
                    {isAdmin && <Link to="/admin" onClick={() => setOpen(false)} className="py-3 text-lg font-display uppercase tracking-wide border-b border-white/5">Admin</Link>}
                    <button onClick={async () => { await logout(); setOpen(false); nav("/"); }} className="py-3 text-left text-lg font-display uppercase tracking-wide text-red-400">Log out</button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setOpen(false)} className="py-3 text-lg font-display uppercase tracking-wide border-b border-white/5">Log in</Link>
                    <Link to="/register" onClick={() => setOpen(false)} className="mt-3 rounded-sm bg-[#DCA846] text-black text-center py-3 font-display uppercase font-semibold">Get Started</Link>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
